import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';
import './AdminPrint.css';

function AdminPrint50Tawi() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Get params from URL
    const cid = searchParams.get('cid');
    const userName = searchParams.get('name');

    // State
    const [userInfo, setUserInfo] = useState(null);
    const [years, setYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [yearlySummary, setYearlySummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [printing, setPrinting] = useState(false);
    const [error, setError] = useState('');

    // Fetch available years
    const fetchData = useCallback(async () => {
        if (!cid) {
            setError('ไม่พบข้อมูลผู้ใช้');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const yearsResponse = await api.get(`/salary/admin/available-years/${cid}`);
            setYears(yearsResponse.data);

            if (yearsResponse.data.length > 0) {
                setSelectedYear(yearsResponse.data[0]);
            }

            setUserInfo({
                cid: cid,
                name: userName || 'ผู้ใช้'
            });
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('ไม่สามารถโหลดข้อมูลได้');
        } finally {
            setLoading(false);
        }
    }, [cid, userName]);

    // Fetch yearly summary when year changes
    const fetchYearlySummary = useCallback(async () => {
        if (!selectedYear || !cid) return;

        try {
            const response = await api.get(`/salary/admin/yearly-summary/${cid}/${selectedYear}`);
            setYearlySummary(response.data);
        } catch (err) {
            console.error('Error fetching yearly summary:', err);
            setYearlySummary(null);
        }
    }, [selectedYear, cid]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchYearlySummary();
    }, [fetchYearlySummary]);

    // Handle print
    const handlePrint = async () => {
        if (!selectedYear) return;

        setPrinting(true);
        try {
            const response = await api.get(`/report/admin/50-tawi/${cid}/${selectedYear}`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Error generating PDF:', err);
            alert('เกิดข้อผิดพลาดในการสร้าง PDF');
        } finally {
            setPrinting(false);
        }
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0.00';
        return Number(num).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    if (loading) {
        return (
            <div className="admin-page">
                <div className="flex-center" style={{ padding: '60px 0' }}>
                    <div className="spinner"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page">
                <div className="alert alert-error">{error}</div>
                <button className="steam-btn steam-btn-secondary" onClick={() => navigate(-1)}>
                    กลับ
                </button>
            </div>
        );
    }

    return (
        <div className="admin-page admin-print-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <Link to="/admin/requests">คำร้องขอเอกสาร</Link>
                <span>/</span>
                <span>พิมพ์ 50 ทวิ</span>
            </div>

            {/* Header */}
            <div className="print-page-header">
                <div className="print-page-icon tawi">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                    </svg>
                </div>
                <div className="print-page-title">
                    <h1>พิมพ์หนังสือรับรอง 50 ทวิ</h1>
                    <p>เลือกปีภาษีที่ต้องการพิมพ์</p>
                </div>
            </div>

            {/* User Info Card */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">ข้อมูลผู้ใช้</div>
                <div className="steam-card-body">
                    <div className="user-info-display">
                        <div className="user-info-avatar">
                            {userInfo?.name?.charAt(0) || 'U'}
                        </div>
                        <div className="user-info-details">
                            <h3>{userInfo?.name}</h3>
                            <span className="user-info-cid">{userInfo?.cid}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Year Selection */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">เลือกปีภาษี</div>
                <div className="steam-card-body">
                    {years.length === 0 ? (
                        <div className="empty-state">
                            <p>ไม่พบข้อมูลเงินเดือน</p>
                        </div>
                    ) : (
                        <div className="year-buttons large">
                            {years.map(year => (
                                <button
                                    key={year}
                                    className={`year-btn ${selectedYear === year ? 'active' : ''}`}
                                    onClick={() => setSelectedYear(year)}
                                >
                                    <span className="year-label">ปี พ.ศ.</span>
                                    <span className="year-value">{year + 543}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Yearly Summary */}
            {selectedYear && yearlySummary && (
                <div className="steam-card mb-3">
                    <div className="steam-card-header">
                        <span>สรุปรายได้ปี {selectedYear + 543}</span>
                    </div>
                    <div className="steam-card-body">
                        <div className="yearly-summary-grid">
                            <div className="summary-item income">
                                <div className="summary-item-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                                    </svg>
                                </div>
                                <div className="summary-item-content">
                                    <span className="summary-item-label">รายได้รวมทั้งปี</span>
                                    <span className="summary-item-value">{formatNumber(yearlySummary.summary?.total_income)}</span>
                                    <span className="summary-item-unit">บาท</span>
                                </div>
                            </div>

                            <div className="summary-item deduction">
                                <div className="summary-item-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M16 18l2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z"/>
                                    </svg>
                                </div>
                                <div className="summary-item-content">
                                    <span className="summary-item-label">รายหักรวมทั้งปี</span>
                                    <span className="summary-item-value">{formatNumber(yearlySummary.summary?.total_deduction)}</span>
                                    <span className="summary-item-unit">บาท</span>
                                </div>
                            </div>

                            <div className="summary-item net">
                                <div className="summary-item-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
                                    </svg>
                                </div>
                                <div className="summary-item-content">
                                    <span className="summary-item-label">รับสุทธิรวม</span>
                                    <span className="summary-item-value">
                                        {formatNumber((yearlySummary.summary?.total_income || 0) - (yearlySummary.summary?.total_deduction || 0))}
                                    </span>
                                    <span className="summary-item-unit">บาท</span>
                                </div>
                            </div>
                        </div>

                        {/* Monthly Chart */}
                        {yearlySummary.monthlyDetail && yearlySummary.monthlyDetail.length > 0 && (
                            <div className="monthly-mini-chart">
                                <h4>รายได้รายเดือน</h4>
                                <div className="mini-chart-container">
                                    {yearlySummary.monthlyDetail.map(month => {
                                        const maxValue = Math.max(...yearlySummary.monthlyDetail.map(m => m.cnet || 0));
                                        const percentage = maxValue > 0 ? ((month.cnet || 0) / maxValue) * 100 : 0;
                                        return (
                                            <div key={month.mm} className="mini-chart-bar">
                                                <div className="bar-fill" style={{ height: `${percentage}%` }}></div>
                                                <span className="bar-label">{month.mm}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Print Actions */}
            <div className="print-actions-footer">
                <button
                    className="steam-btn steam-btn-secondary"
                    onClick={() => navigate(-1)}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    กลับ
                </button>
                <button
                    className="steam-btn steam-btn-success print-main-btn"
                    onClick={handlePrint}
                    disabled={!selectedYear || printing}
                >
                    {printing ? (
                        <>
                            <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}></div>
                            กำลังสร้าง PDF...
                        </>
                    ) : (
                        <>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M6 9V2h12v7"/>
                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                                <rect x="6" y="14" width="12" height="8"/>
                            </svg>
                            พิมพ์ 50 ทวิ ปี {selectedYear ? selectedYear + 543 : ''}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

export default AdminPrint50Tawi;
